import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Web3Service } from '@/services/web3.service';
import { StateService } from '@/services/state.service';

export interface SignInOption {
  address: string;
  label: string;
}

@Component({
  selector: 'app-signature-explanation-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  template: `
    <div 
      class="modal-overlay"
      [class.active]="modalActive"
      [class]="modalActive ? 'modal' : ''">
      
      <div class="modal">
        <button class="close-modal" (click)="decline()">×</button>
        
        <div class="punk-info">
          <h2>Signature Required</h2>
          <p>
          Signing in allows you to access our off-chain authenticated features, including Confidential Bids and the ability to use a <a href="https://delegate.xyz" target="_blank" rel="noopener noreferrer">delegate.xyz</a> wallet for additional marketplace features.
          </p>
          <div *ngIf="signInOptions.length > 1" class="sign-in-as">
            <label for="sign-in-identity">Sign in as:</label>
            <ng-select
              id="sign-in-identity"
              class="identity-select"
              [items]="signInOptions"
              bindValue="address"
              bindLabel="label"
              [(ngModel)]="selectedIdentityAddress"
              [clearable]="false"
              [searchable]="false"
              placeholder="Choose identity">
            </ng-select>
          </div>
        </div>
        
        <div class="form-actions">
          <button class="cancel" (click)="decline()">Skip</button>
          <p class="skip-note">You can refuse these advanced features by clicking Skip and continue using the marketplace as-is.</p>
          <button class="submit" (click)="accept()">Sign</button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./signature-explanation-modal.component.scss']
})
export class SignatureExplanationModalComponent implements OnInit, OnDestroy {
  modalActive = false;
  signInOptions: SignInOption[] = [];
  selectedIdentityAddress: string = '';
  hasNoIncomingDelegations = false;
  private subscription = new Subscription();

  constructor(
    private web3Service: Web3Service,
    private stateSvc: StateService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.web3Service.signatureExplanationModalActive$.subscribe(active => {
        this.modalActive = active;
      })
    );
    this.subscription.add(
      combineLatest([
        this.stateSvc.walletAddress$,
        this.stateSvc.incomingDelegations$
      ]).pipe(
        map(([wallet, incoming]) => {
          const options: SignInOption[] = [];
          const seen = new Set<string>();
          if (wallet) {
            const walletLower = wallet.toLowerCase();
            options.push({ address: wallet, label: 'Myself' });
            seen.add(walletLower);
            (incoming || []).forEach(d => {
              const fromLower = d.from.toLowerCase();
              if (!seen.has(fromLower)) {
                seen.add(fromLower);
                options.push({ address: d.from, label: this.shortAddress(d.from) });
              }
            });
          }
          return { options, hasNoIncoming: !incoming?.length };
        })
      ).subscribe(({ options, hasNoIncoming }) => {
        this.signInOptions = options;
        this.hasNoIncomingDelegations = hasNoIncoming;
        if (options.length > 0 && (!this.selectedIdentityAddress || !options.find(o => o.address === this.selectedIdentityAddress))) {
          this.selectedIdentityAddress = options[0].address;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  accept(): void {
    this.web3Service.acceptSignatureExplanation(this.selectedIdentityAddress || undefined);
  }

  decline(): void {
    this.web3Service.declineSignatureExplanation();
  }

  private shortAddress(addr: string): string {
    if (!addr || addr.length < 10) return addr;
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
  }
}

